import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { DataBuild, BuildStatus, STATUS_COLORS, Definition } from '../types';

const DataBuildsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dataBuilds, setDataBuilds] = useState<DataBuild[]>([]);
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBuildCreatedAlert, setShowBuildCreatedAlert] = useState(false);
  const [newBuildId, setNewBuildId] = useState<string | null>(null);

  useEffect(() => {
    loadDataBuilds();
  }, []);

  // Check if we're coming from a successful build creation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const buildCreated = searchParams.get('buildCreated');
    const buildId = searchParams.get('buildId');
    
    if (buildCreated === 'true' && buildId) {
      setShowBuildCreatedAlert(true);
      setNewBuildId(buildId);
      
      // Clear the URL parameters
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const loadDataBuilds = async () => {
    try {
      setLoading(true);
      const [data, defs] = await Promise.all([
        networkManager.getDataBuilds(),
        networkManager.getDefinitions()
      ]);
      // Ensure data is always an array
      setDataBuilds(Array.isArray(data) ? data : []);
      setDefinitions(Array.isArray(defs) ? defs : []);
    } catch (err) {
      setError('Failed to load data builds');
      console.error('Error loading data builds:', err);
      // Set empty array on error to prevent null issues
      setDataBuilds([]);
      setDefinitions([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    return STATUS_COLORS[status.toLowerCase() as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800';
  };

  const formatExecutionTime = (time: number) => {
    if (time < 1000) return `${time}ms`;
    return `${(time / 1000).toFixed(1)}s`;
  };

  const formatDate = (dateString: string) => {
    // Parse the date string and treat it as UTC
    const date = new Date(dateString);
    
    // Get UTC components to avoid timezone conversion issues
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    
    return `${day}/${month}/${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const getDefinitionTitle = (definitionId: string) => {
    const definition = definitions.find(def => def._id === definitionId);
    return definition ? definition.title : definitionId;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading data builds...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <style dangerouslySetInnerHTML={{ __html: `
        .truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .table-container {
          width: 100%;
          max-width: 100%;
        }
        .table {
          width: 100%;
          table-layout: fixed;
          border-collapse: collapse;
        }
        .table th,
        .table td {
          padding: 0.75rem;
          text-align: left;
          vertical-align: top;
          word-wrap: break-word;
          overflow: hidden;
        }
        .table th {
          background-color: #f9fafb;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }
        .table td {
          border-bottom: 1px solid #f3f4f6;
        }
        .w-48 { width: 12rem; }
        .w-80 { width: 20rem; }
        .w-32 { width: 8rem; }
        .w-40 { width: 10rem; }
      `}} />

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Data Builds</h1>
            <p className="text-gray-600 mt-1">Monitor and manage your data build processes</p>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => navigate('/data-comparison')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 hover:bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-lg transition-all duration-200"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Compare Builds
            </button>
            <button 
              onClick={() => navigate('/new-build')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 hover:border-blue-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Build
            </button>
          </div>
        </div>
      </div>

      {/* Build Created Success Alert */}
      {showBuildCreatedAlert && newBuildId && (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">
                  ✅ Build created successfully!
                </p>
                <p className="text-sm text-green-600">
                  Your data build has been created and is now processing.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to={`/data-builds/${newBuildId}`}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 hover:text-green-800 transition-colors"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Click here to view build details
              </Link>
              <button
                onClick={() => setShowBuildCreatedAlert(false)}
                className="text-green-400 hover:text-green-600 transition-colors"
                title="Dismiss alert"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Table */}
      <div className="table-container">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="w-48">Title</th>
                <th className="w-80">Description & Details</th>
                <th className="w-48">Definition</th>
                <th className="w-32">Author</th>
                <th className="w-40">Created At</th>
              </tr>
            </thead>
            <tbody>
              {!dataBuilds || dataBuilds.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <p className="text-gray-500 text-lg font-medium">No data builds found</p>
                      <p className="text-gray-400 text-sm">Get started by creating your first data build</p>
                    </div>
                  </td>
                </tr>
              ) : (
                dataBuilds.map((build) => (
                  <tr key={build._id}>
                                         <td>
                       <div className="flex items-center space-x-2">
                         {build.status === 'success' ? (
                           <div title="Success">
                             <svg className="h-5 w-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                             </svg>
                           </div>
                         ) : build.status === 'failed' ? (
                           <div title="Failed">
                             <svg className="h-5 w-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                             </svg>
                           </div>
                         ) : build.status === 'running' ? (
                           <div title="Running">
                             <svg className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                             </svg>
                           </div>
                         ) : (
                           <div title="Pending">
                             <svg className="h-5 w-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                             </svg>
                           </div>
                         )}
                         <div className="min-w-0 flex-1">
                           <Link
                             to={`/data-builds/${build._id}`}
                             className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors truncate block"
                             title={build.title}
                           >
                             {build.title}
                           </Link>
                           <p className="text-sm text-gray-500 truncate" title={build._id}>
                             ID: {build._id}
                           </p>
                         </div>
                       </div>
                     </td>
                                         <td>
                       <div className="min-w-0">
                         <div className="flex items-start space-x-2">
                           <svg className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                           </svg>
                           <div className="flex-1 min-w-0">
                             <p 
                               className="text-sm text-gray-900 leading-relaxed font-medium truncate" 
                               title={build.description || 'No description'}
                             >
                               {build.description || 'No description available'}
                             </p>
                           </div>
                         </div>
                         <p className="text-xs text-gray-500 truncate mt-1" title={build.spreadsheet}>
                           Spreadsheet: {' '}
                           <a 
                             href={`https://docs.google.com/spreadsheets/d/${build.spreadsheet}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-blue-600 hover:text-blue-800 hover:underline"
                           >
                             {build.spreadsheet.substring(0, 20)}...
                           </a>
                         </p>
                       </div>
                     </td>
                                         <td>
                       <div className="flex items-center space-x-2 min-w-0">
                         <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                         </svg>
                         <div className="min-w-0 flex-1">
                           <Link
                             to={`/definitions/${build.definitionID}`}
                             className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors truncate block"
                             title={getDefinitionTitle(build.definitionID)}
                           >
                             {getDefinitionTitle(build.definitionID)}
                           </Link>
                         </div>
                       </div>
                     </td>
                                         <td>
                       <div className="flex items-center space-x-2 min-w-0">
                         <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                         </svg>
                         <p className="text-sm text-gray-600 truncate" title={build.author?.name || 'Unknown'}>
                           {build.author?.name || 'Unknown'}
                         </p>
                       </div>
                     </td>
                                         <td>
                       <div className="min-w-0">
                         <p className="text-sm text-gray-600 truncate" title={formatDate(build.createdAt)}>
                           {formatDate(build.createdAt)}
                         </p>
                         <p className="text-xs text-gray-500 truncate" title={`Exec: ${formatExecutionTime(build.executionTime)}`}>
                           Exec: {formatExecutionTime(build.executionTime)}
                         </p>
                       </div>
                     </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DataBuildsPage;
